import { useCallback, useRef, useState } from 'react';

import {
    AUTH_SOURCE_PARAM_KEY,
    AUTH_TYPE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';

export interface SocialAuthReturn {
    /** The reader is back from a social round-trip raised by this gate. */
    authReturn: boolean;
    /** That round-trip created the account, rather than signing one in. */
    signUpReturn: boolean;
}

const NO_RETURN: SocialAuthReturn = { authReturn: false, signUpReturn: false };

/**
 * Whether this page load is the far side of a social round-trip that *this*
 * gate started, read from the params the redirect appends.
 *
 * Read once and spent once. The params are captured at mount because
 * `useAuthEvent` strips them from the URL immediately afterwards — but the
 * gates are mounted site-wide for the whole visit, so a flag kept past the
 * opening it describes is read again by every later enquiry, save or download
 * in that visit. That greets a returning member as new, re-enters the auth
 * segment for a journey with no auth step, and advances the passkey skip
 * cadence towards re-offering an ask the reader already answered.
 *
 * `take()` returns the signal and clears it, so it describes exactly one
 * opening. `seen` is the same value as state, for the render path that needs it
 * after the taking is done.
 *
 * `spend()` clears without reporting, for the ends of a journey that never
 * reach a success — a dismissal, a failed resume, a session that would not
 * restore. Without it the signal outlives the opening it describes just as
 * surely as if it were never consumed at all, and the next successful send in
 * that visit inherits it.
 */
export const useSocialAuthReturn = (
    authSourceValue: string,
): {
    take: () => SocialAuthReturn;
    spend: () => void;
    seen: SocialAuthReturn;
} => {
    const pendingRef = useRef<SocialAuthReturn>(
        (() => {
            if (typeof window === 'undefined') return NO_RETURN;
            const params = new URLSearchParams(window.location.search);
            const authReturn =
                params.get(AUTH_SOURCE_PARAM_KEY) === authSourceValue;

            return {
                authReturn,
                signUpReturn:
                    authReturn && params.get(AUTH_TYPE_PARAM_KEY) === 'sign-up',
            };
        })(),
    );
    const [seen, setSeen] = useState<SocialAuthReturn>(NO_RETURN);

    const take = useCallback(() => {
        const pending = pendingRef.current;
        if (pending.authReturn) {
            pendingRef.current = NO_RETURN;
            setSeen(pending);
        }

        return pending;
    }, []);

    const spend = useCallback(() => {
        pendingRef.current = NO_RETURN;
    }, []);

    return { take, spend, seen };
};
